/**
 * TBMuktBharat Competition Detail Page
 * Shows competition details and allows submission
 */

import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom'
import { GovLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout'
import {
  Calendar, Users, Trophy, Clock, Award, ArrowLeft, Share2, Heart,
  CheckCircle, AlertCircle, FileText, MapPin, Loader2, ExternalLink
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import competitionService, { Competition, CompetitionSubmission } from '@/services/competitionService'
import { useAuthStore } from '@/stores/authStore'

// ================================================================================
// TYPE CONFIG
// ================================================================================
const COMPETITION_TYPES: Record<string, { label: string; icon: string; description: string }> = {
  POSTER: { label: 'Poster', icon: '🎨', description: 'Create a creative poster about TB awareness' },
  QUIZ: { label: 'Quiz', icon: '🧠', description: 'Test your knowledge about tuberculosis' },
  ESSAY: { label: 'Essay', icon: '📝', description: 'Write an essay on TB-related topics' },
  DRAWING: { label: 'Drawing', icon: '🖌️', description: 'Express through art and drawing' },
  SLOGAN: { label: 'Slogan', icon: '💬', description: 'Create catchy slogans for TB awareness' },
}

const STATUS_CONFIG: Record<string, { label: string; color: string; canSubmit: boolean }> = {
  DRAFT: { label: 'Draft', color: 'bg-gray-100 text-gray-700', canSubmit: false },
  ACTIVE: { label: 'Active', color: 'bg-green-100 text-green-700', canSubmit: true },
  CLOSED: { label: 'Closed', color: 'bg-red-100 text-red-700', canSubmit: false },
  ARCHIVED: { label: 'Archived', color: 'bg-gray-100 text-gray-500', canSubmit: false },
}

// ================================================================================
// MAIN COMPONENT
// ================================================================================

const CompetitionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated, user } = useAuthStore()

  const [competition, setCompetition] = useState<Competition | null>(null)
  const [submission, setSubmission] = useState<CompetitionSubmission | null>(null)
  const [results, setResults] = useState<CompetitionSubmission[]>([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [activeTab, setActiveTab] = useState<'details' | 'results' | 'submission'>('details')

  useEffect(() => {
    if (id) {
      fetchCompetition()
    }
  }, [id])

  const fetchCompetition = async () => {
    setLoading(true)
    try {
      const data = await competitionService.getCompetitionById(Number(id))
      setCompetition(data)

      // If closed, fetch results
      if (data.status === 'CLOSED') {
        fetchResults()
      }

      // Check if user has submitted
      if (isAuthenticated && user) {
        try {
          const userSubmission = await competitionService.getUserSubmissionForCompetition(Number(id))
          setSubmission(userSubmission)
        } catch {
          // User hasn't submitted yet
        }
      }
    } catch (error) {
      console.error('Failed to fetch competition:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchResults = async () => {
    try {
      const data = await competitionService.getResults(Number(id))
      setResults(data)
    } catch (error) {
      console.error('Failed to fetch results:', error)
    }
  }

  const handleSubmit = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/competitions/${id}/submit` } })
      return
    }
    navigate(`/competitions/${id}/submit`)
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'TBD'
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  }

  if (loading) {
    return (
      <GovLayout>
        <div className="flex justify-center items-center min-h-[400px]">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      </GovLayout>
    )
  }

  if (!competition) {
    return (
      <GovLayout>
        <GovEmptyState
          icon="🏆"
          title="Competition not found"
          description="The competition you're looking for doesn't exist or has been removed."
          action={{ label: 'Browse Competitions', onClick: () => navigate('/competitions') }}
        />
      </GovLayout>
    )
  }

  const typeConfig = COMPETITION_TYPES[competition.type] || COMPETITION_TYPES.POSTER
  const statusConfig = STATUS_CONFIG[competition.status] || STATUS_CONFIG.ACTIVE

  return (
    <GovLayout>
      <div className="max-w-4xl mx-auto">
        {/* Back Button — returns to where the user came from (admin list or public list),
            falling back to the public list /competitions when no origin was passed. */}
        <Link
          to={(location.state as { from?: string } | null)?.from || '/competitions'}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Competitions</span>
        </Link>

        {/* Banner */}
        {competition.bannerImageUrl ? (
          <div className="h-48 md:h-64 -mx-4 md:mx-0 md:rounded-xl overflow-hidden mb-6">
            <img
              src={competition.bannerImageUrl}
              alt={competition.title}
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="h-48 md:h-64 -mx-4 md:mx-0 md:rounded-xl bg-gradient-to-br from-blue-500 via-cyan-500 to-green-500 flex items-center justify-center mb-6">
            <span className="text-6xl">{typeConfig.icon}</span>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className={`text-sm px-3 py-1 rounded-full ${statusConfig.color}`}>
                {statusConfig.label}
              </span>
              {competition.isFeatured && (
                <span className="text-sm px-3 py-1 rounded-full bg-amber-100 text-amber-700">
                  ⭐ Featured
                </span>
              )}
              <span className="text-sm px-3 py-1 rounded-full bg-blue-100 text-blue-700">
                {typeConfig.icon} {typeConfig.label}
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              {competition.title}
            </h1>
          </div>

          <div className="flex items-center gap-2">
            <button className="p-2 rounded-lg hover:bg-gray-100" title="Share">
              <Share2 className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <GovCard className="text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Users className="w-4 h-4 text-blue-500" />
              <span className="text-sm text-gray-500">Participants</span>
            </div>
            <div className="text-xl font-bold text-gray-900">
              {competition.currentParticipants || 0}
              {competition.maxParticipants && (
                <span className="text-sm font-normal text-gray-400">/{competition.maxParticipants}</span>
              )}
            </div>
          </GovCard>

          <GovCard className="text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-amber-500" />
              <span className="text-sm text-gray-500">Days Left</span>
            </div>
            <div className="text-xl font-bold text-gray-900">
              {competition.daysRemaining ?? '-'}
            </div>
          </GovCard>

          <GovCard className="text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Calendar className="w-4 h-4 text-green-500" />
              <span className="text-sm text-gray-500">Deadline</span>
            </div>
            <div className="text-sm font-medium text-gray-900">
              {formatDate(competition.endDate)}
            </div>
          </GovCard>

          <GovCard className="text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Award className="w-4 h-4 text-purple-500" />
              <span className="text-sm text-gray-500">Results</span>
            </div>
            <div className="text-sm font-medium text-gray-900">
              {formatDate(competition.resultDate)}
            </div>
          </GovCard>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          {[
            { key: 'details', label: 'Details' },
            { key: 'results', label: 'Results', show: competition.status === 'CLOSED' },
            { key: 'submission', label: 'My Submission', show: isAuthenticated && submission },
          ].filter(tab => tab.show !== false).map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as typeof activeTab)}
              className={`px-4 py-2 font-medium transition-colors ${
                activeTab === tab.key
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'details' && (
            <motion.div
              key="details"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              {/* Description */}
              <GovCard className="mb-4">
                <h2 className="font-bold text-gray-900 mb-2">About This Competition</h2>
                <p className="text-gray-600 whitespace-pre-line">
                  {competition.description}
                </p>
              </GovCard>

              {/* Prize */}
              {competition.prizeDescription && (
                <GovCard className="mb-4">
                  <h2 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-amber-500" />
                    Prizes
                  </h2>
                  <p className="text-gray-600">{competition.prizeDescription}</p>
                </GovCard>
              )}

              {/* Rules */}
              {competition.rules && (
                <GovCard className="mb-4">
                  <h2 className="font-bold text-gray-900 mb-2">Rules & Guidelines</h2>
                  <div className="text-gray-600 whitespace-pre-line">
                    {competition.rules}
                  </div>
                </GovCard>
              )}

              {/* Guidelines */}
              {competition.guidelines && (
                <GovCard className="mb-4">
                  <h2 className="font-bold text-gray-900 mb-2">Submission Guidelines</h2>
                  <div className="text-gray-600 whitespace-pre-line">
                    {competition.guidelines}
                  </div>
                </GovCard>
              )}

              {/* Allowed File Types */}
              {competition.allowedFileTypes && (
                <GovCard className="mb-4">
                  <h2 className="font-bold text-gray-900 mb-2">Accepted File Formats</h2>
                  <div className="flex flex-wrap gap-2">
                    {competition.allowedFileTypes.split(',').map(type => (
                      <span key={type} className="px-3 py-1 bg-gray-100 rounded-full text-sm">
                        {type.trim()}
                      </span>
                    ))}
                  </div>
                  {competition.maxFileSize && (
                    <p className="text-sm text-gray-500 mt-2">
                      Maximum file size: {(competition.maxFileSize / 1024 / 1024).toFixed(0)}MB
                    </p>
                  )}
                </GovCard>
              )}

              {/* Submit Button */}
              {statusConfig.canSubmit && !competition.isExpired && (
                <div className="mt-6">
                  {submission ? (
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-green-700 mb-2">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">You have already submitted</span>
                      </div>
                      <p className="text-sm text-green-600">
                        Submitted on {formatDate(submission.submittedAt)}
                      </p>
                      <button
                        onClick={() => setActiveTab('submission')}
                        className="mt-2 text-blue-600 hover:underline text-sm"
                      >
                        View your submission →
                      </button>
                    </div>
                  ) : (
                    <GovButton
                      variant="primary"
                      className="w-full"
                      onClick={handleSubmit}
                      disabled={!isAuthenticated}
                    >
                      {isAuthenticated ? 'Submit Your Entry' : 'Login to Submit'}
                    </GovButton>
                  )}
                </div>
              )}

              {/* Competition Ended */}
              {competition.status === 'CLOSED' && (
                <div className="mt-6 bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-gray-700 mb-2">
                    <AlertCircle className="w-5 h-5" />
                    <span className="font-medium">Competition has ended</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Submissions are no longer accepted. Check the Results tab for winners.
                  </p>
                </div>
              )}
            </motion.div>
          )}

          {activeTab === 'results' && competition.status === 'CLOSED' && (
            <motion.div
              key="results"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              {results.length === 0 ? (
                <GovEmptyState
                  icon="🏆"
                  title="Results pending"
                  description="Results will be announced soon. Check back later."
                />
              ) : (
                <div className="space-y-4">
                  {results.map((result, index) => (
                    <GovCard key={result.id} className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold ${
                        result.rank === 1 ? 'bg-amber-100 text-amber-700' :
                        result.rank === 2 ? 'bg-gray-200 text-gray-700' :
                        result.rank === 3 ? 'bg-orange-100 text-orange-700' :
                        'bg-gray-100 text-gray-500'
                      }`}>
                        {result.rank === 1 ? '🥇' : result.rank === 2 ? '🥈' : result.rank === 3 ? '🥉' : result.rank}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{result.fullName}</div>
                        <div className="text-sm text-gray-500">{result.schoolCollegeName || result.state}</div>
                      </div>
                      {result.certificateUrl && (
                        <a
                          href={result.certificateUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-blue-600 hover:underline text-sm"
                        >
                          <FileText className="w-4 h-4" />
                          Certificate
                        </a>
                      )}
                    </GovCard>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {activeTab === 'submission' && submission && (
            <motion.div
              key="submission"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <GovCard>
                <h2 className="font-bold text-gray-900 mb-4">Your Submission</h2>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-gray-500">Title</label>
                    <p className="font-medium">{submission.submissionTitle || 'Untitled'}</p>
                  </div>

                  {submission.submissionDescription && (
                    <div>
                      <label className="text-sm text-gray-500">Description</label>
                      <p className="text-gray-600">{submission.submissionDescription}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-500">Status</label>
                      <p className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm ${
                        submission.status === 'APPROVED' ? 'bg-green-100 text-green-700' :
                        submission.status === 'REJECTED' ? 'bg-red-100 text-red-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {submission.status === 'APPROVED' && <CheckCircle className="w-4 h-4" />}
                        {submission.status === 'REJECTED' && <AlertCircle className="w-4 h-4" />}
                        {submission.statusDisplayName}
                      </p>
                    </div>

                    {submission.rank && (
                      <div>
                        <label className="text-sm text-gray-500">Rank</label>
                        <p className="font-medium text-lg">
                          #{submission.rank}
                        </p>
                      </div>
                    )}
                  </div>

                  {submission.fileUrl && (
                    <div>
                      <label className="text-sm text-gray-500">Your File</label>
                      <a
                        href={submission.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-blue-600 hover:underline"
                      >
                        <FileText className="w-5 h-5" />
                        {submission.fileName || 'View Submission'}
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  )}

                  {submission.feedback && (
                    <div>
                      <label className="text-sm text-gray-500">Feedback</label>
                      <p className="text-gray-600 bg-gray-50 rounded-lg p-3">
                        {submission.feedback}
                      </p>
                    </div>
                  )}

                  {submission.certificateUrl && (
                    <div className="pt-4 border-t border-gray-100">
                      <a
                        href={submission.certificateUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                      >
                        <Award className="w-5 h-5" />
                        Download Certificate
                      </a>
                    </div>
                  )}
                </div>
              </GovCard>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </GovLayout>
  )
}

export default CompetitionDetailPage